// `createApiClient` — build a typed fetch client from a registry tree.
//
// The returned client mirrors the registry's nesting, so a route at
// `auctions.detail` is called `api.auctions.detail.get({ params: { id } })`.
// TypeScript only exposes the verbs an entry declares, and only requires
// `params` when the entry has path params. Each call returns the standard
// envelope; tighten the data type per call with the `<T>` generic.

import type { ErrorEnvelope, SuccessEnvelope } from '../error-envelope';
import { parseResponse } from '../parse-response';
import { applyResponseStatusHandlers } from '../response-status-handlers';
import { buildUrl } from './build-url';
import { isRouteEntry } from './define-route';
import type { HttpVerb, InferResponse, QueryShape, RegistryTree, RouteEntry } from './types';

/** The discriminated envelope every client call resolves to. */
export type ApiEnvelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

/** Args for a verb call. `params` is required only when the route has path params. */
type CallArgs<P extends Record<string, string>> = (keyof P extends never
  ? { params?: undefined }
  : { params: { [K in keyof P]: string | string[] } }) & {
  query?: QueryShape;
  body?: unknown;
  /** Per-call `RequestInit` (e.g. `cache: 'no-store'`, `signal`). The verb/body/JSON header always win over this. */
  init?: RequestInit;
};

/** Loose args shape used by the internal request executor (post type-erasure). */
type CallExecArgs = {
  params?: Record<string, string | string[]>;
  query?: QueryShape;
  body?: unknown;
  init?: RequestInit;
};

/**
 * A single verb method. Optional args only when the route has no path params.
 *
 * `R` is the response DTO carried by the entry's `responseType<DTO>()` marker
 * (ADR 027). The call's `<T>` generic defaults to `R`, so an entry with a marker
 * infers its response with no per-call generic; an entry without one keeps
 * `R = unknown`, i.e. the prior behavior. The `<T>` override remains for the
 * un-migrated tail (the ESLint response-generic ban flags it once a marker exists).
 *
 * Calling it resolves to the standard `{ success, data }` envelope. The `.raw`
 * escape hatch issues the SAME typed request (registry-typed path/params/verb)
 * but returns the raw `Response` — for the bespoke minority of routes the
 * envelope can't model: non-standard top-level bodies, HTTP-status branching
 * (404 → null, `status`-in-error strings), `content-type` checks (502/504 HTML
 * cold-starts), and binary/blob downloads. Both forms reference the same dotted
 * registry key, so dead-route detection sees a `.raw` caller exactly like a
 * normal one.
 */
type VerbMethod<P extends Record<string, string>, R = unknown> = (keyof P extends never
  ? <T = R>(args?: CallArgs<P>) => Promise<ApiEnvelope<T>>
  : <T = R>(args: CallArgs<P>) => Promise<ApiEnvelope<T>>) & {
  raw: keyof P extends never
    ? (args?: CallArgs<P>) => Promise<Response>
    : (args: CallArgs<P>) => Promise<Response>;
  /**
   * The typed envelope WITH the HTTP status beside it (BL-1981). For a read
   * that must tell "this record does not exist" (404, final) from "the read
   * failed" (retryable) without dropping to `.raw()`, which returns an untyped
   * `Response` and so cannot carry the DTO. Not generic: the body is always the
   * entry's marked DTO — there is no `<T>` to assert.
   */
  withStatus: keyof P extends never
    ? (args?: CallArgs<P>) => Promise<StatusEnvelope<R>>
    : (args: CallArgs<P>) => Promise<StatusEnvelope<R>>;
};

/** A parsed envelope plus the HTTP status it arrived with (`.withStatus()`). */
export interface StatusEnvelope<T> {
  status: number;
  body: ApiEnvelope<T>;
}

/**
 * The verb methods exposed for one route entry (lowercased verb names). Each
 * verb's response DTO is pulled from its {@link VerbSpec} via `InferResponse`,
 * so `api.lots.detail.get()` returns `ApiEnvelope<LotDetail>` when the entry
 * carries a `responseType<LotDetail>()` marker.
 */
type VerbMethods<P extends Record<string, string>, M> = {
  [V in keyof M as Lowercase<V & string>]: VerbMethod<P, InferResponse<M[V]>>;
};

/** The client shape, mirroring the registry tree. */
export type ApiClient<T> = {
  [K in keyof T]: T[K] extends RouteEntry<infer P, infer M>
    ? VerbMethods<P, M>
    : T[K] extends RegistryTree
      ? ApiClient<T[K]>
      : never;
};

export interface ApiClientOptions {
  /** Absolute base URL (e.g. for the mobile app). Defaults to '' (relative). */
  baseUrl?: string;
  /** Fetch implementation. Defaults to the global `fetch`. */
  fetch?: typeof fetch;
  /** Extra `RequestInit` merged into every call (e.g. `credentials`). */
  init?: RequestInit;
  /**
   * Invoked (best-effort, fire-and-forget) whenever a call resolves with HTTP
   * **401** — i.e. no valid session. Each app injects its own handler to turn a
   * stale/expired session into a login redirect instead of a raw "Unauthorized"
   * error (BUG-802). Fires for BOTH envelope calls and the `.raw()` escape
   * hatch — the hook lives in `makeRawCall`, below `parseResponse`, because
   * nearly every bidder action (bid placement, watchlist, …) uses `.raw()`.
   * Only 401 triggers it; 403 (authenticated-but-forbidden) does not — that's a
   * permission denial, not a re-auth signal. The Response is still returned to
   * the caller unchanged; this is a side-channel, not a swallow.
   */
  onUnauthorized?: (res: Response) => void;
  /**
   * Invoked (best-effort, fire-and-forget) whenever a call resolves with HTTP
   * **418** — the "account unavailable" sentinel a request emits when the
   * session funnel resolves a BANNED customer (BUG-973). Each app injects its
   * own handler to route the user to a dedicated "account unavailable" screen
   * instead of the generic stale-session login bounce (`onUnauthorized`). Fires
   * for BOTH envelope calls and the `.raw()` escape hatch — the hook lives in
   * `makeRawCall`, below `parseResponse`, because nearly every bidder action
   * (bid placement, watchlist, …) uses `.raw()`. Sibling of `onUnauthorized`:
   * 401 = no session → login; 418 = banned session → account-unavailable. The
   * Response is still returned to the caller unchanged; this is a side-channel.
   */
  onAccountUnavailable?: (res: Response) => void;
  /**
   * Opt-in (BUG-1823): resolve a request that never reached the server —
   * `fetch` itself rejected (offline, DNS, a CORS abort), so there is no
   * Response to parse — as `{ success: false, error }` instead of rejecting.
   * The envelope call resolves with `error` set to this option's `error`;
   * `.withStatus()` resolves with status `0`. A caller's own abort still
   * rejects, and `.raw()` always rejects (its caller owns the Response).
   *
   * Off by default, and the admin client leaves it off on purpose: each admin
   * screen has its own network-error wording ("Could not load this tax rate…",
   * "Could not send the announcement.") and reaches it through the rejection —
   * `useRecordById`'s `networkErrorMessage`, or the hook's own `catch`. Turning
   * this on there would replace every screen's wording with one generic line.
   * The bidder client turns it on: its trays and composer branch only on the
   * resolved envelope, so a rejection left them on an endless skeleton or a
   * silent failed send.
   */
  resolveNetworkFailures?: { error: string };
}

/**
 * Whether a fetch rejection is the caller cancelling its own request (an
 * `AbortSignal`), which must keep rejecting: hooks abort superseded requests
 * on unmount and recognise the AbortError themselves.
 *
 * @param err - What `fetch` rejected with.
 * @param signal - The request's signal, if it had one.
 * @returns `true` for a caller-initiated abort.
 */
function isCallerAbort(err: unknown, signal: AbortSignal | null | undefined): boolean {
  return signal?.aborted === true || (err as { name?: unknown } | null)?.name === 'AbortError';
}

/**
 * Build a typed client from a registry tree.
 *
 * @sideEffect Each generated method performs an HTTP `fetch`.
 */
export function createApiClient<T extends RegistryTree>(
  registry: T,
  options: ApiClientOptions = {},
): ApiClient<T> {
  const baseUrl = options.baseUrl ?? '';
  // Resolve fetch at CALL time, not build time, so test mocks (vi.spyOn(global,
  // 'fetch')) and any later reassignment are honored.
  const resolveFetch = (): typeof fetch => options.fetch ?? globalThis.fetch;
  const resolveNetworkFailures = options.resolveNetworkFailures;

  // A rejected fetch never produced a Response, so it is a network failure,
  // not a server answer. Opted in, it resolves as the error envelope every
  // caller already branches on (BUG-1823); the cause is logged, not swallowed,
  // since the envelope has no room for it. Opted out, or on the caller's own
  // abort, the rejection propagates unchanged. Only fetch's own rejection
  // counts: `makeRawCall` marks it, so a throw from a status handler is never
  // relabelled a network failure.
  const fetchRejections = new WeakSet<object>();
  // Rethrows unchanged, so `.raw()` callers see exactly what fetch threw.
  const markFetchRejection = (err: unknown): never => {
    if (typeof err === 'object' && err !== null) fetchRejections.add(err);
    throw err;
  };
  const networkFailure = (err: unknown, args?: CallExecArgs): ErrorEnvelope => {
    if (!resolveNetworkFailures) throw err;
    if (typeof err !== 'object' || err === null || !fetchRejections.has(err)) throw err;
    if (isCallerAbort(err, args?.init?.signal ?? options.init?.signal)) throw err;
    console.warn('[api-client] request never reached the server', err);
    return { success: false, error: resolveNetworkFailures.error };
  };

  const buildNode = (node: RegistryTree): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const [name, child] of Object.entries(node)) {
      if (isRouteEntry(child)) {
        out[name] = buildVerbMethods(child);
      } else {
        out[name] = buildNode(child);
      }
    }
    return out;
  };

  const buildVerbMethods = (entry: RouteEntry<Record<string, string>>): Record<string, unknown> => {
    const methods: Record<string, unknown> = {};
    for (const verb of Object.keys(entry.methods) as HttpVerb[]) {
      // The callable resolves to the standard envelope; `.raw` exposes the
      // underlying Response for the bespoke routes the envelope can't model;
      // `.withStatus` is the envelope plus its HTTP status (BL-1981).
      // `.raw` keeps rejecting on a network failure — its callers own the
      // Response and there is no envelope to resolve to.
      const raw = makeRawCall(entry, verb);
      const call = async (args?: CallExecArgs) => {
        let res: Response;
        try {
          res = await raw(args);
        } catch (err) {
          return networkFailure(err, args);
        }
        return parseResponse(res);
      };
      // A network failure has no HTTP status; 0 is the Fetch spec's own
      // status for one (`Response.error()`).
      const withStatus = async (args?: CallExecArgs) => {
        let res: Response;
        try {
          res = await raw(args);
        } catch (err) {
          return { status: 0, body: networkFailure(err, args) };
        }
        return { status: res.status, body: await parseResponse(res) };
      };
      Object.assign(call, { raw, withStatus });
      methods[verb.toLowerCase()] = call;
    }
    return methods;
  };

  // Issue the typed request and return the raw Response (no envelope parsing).
  // Shared by the envelope call (which then runs `parseResponse`) and `.raw`.
  const makeRawCall =
    (entry: RouteEntry<Record<string, string>>, verb: HttpVerb) =>
    (args?: CallExecArgs): Promise<Response> => {
      const url = baseUrl + buildUrl(entry, { params: args?.params, query: args?.query });
      const hasBody = args?.body !== undefined;
      // Raw bodies (FormData multipart upload, Blob, string, URLSearchParams)
      // pass through untouched — no JSON.stringify, no forced JSON header (the
      // browser sets the multipart boundary for FormData).
      const rawBody = hasBody && isRawBody(args!.body);
      return resolveFetch()(url, {
        ...options.init,
        ...args?.init,
        method: verb, // after the spreads so neither init can override the verb
        headers: {
          ...options.init?.headers,
          ...(args?.init?.headers ?? {}),
          // JSON content-type wins last for a JSON-stringified body — you can't
          // send an object body under a different type. Raw bodies (FormData)
          // never reach here, so init.headers controls their content-type.
          ...(hasBody && !rawBody ? { 'Content-Type': 'application/json' } : {}),
        },
        body: !hasBody ? undefined : rawBody ? (args!.body as RequestInit['body']) : JSON.stringify(args!.body),
      })
        // Before the status handlers, so only fetch's own rejection is marked.
        .catch(markFetchRejection)
        .then((res) => {
        // 401 = no valid session. Fire the app's re-auth hook here (in
        // makeRawCall) so it covers `.raw()` callers too — parseResponse, which
        // only the envelope path runs, would miss them (BUG-802). The Response
        // is returned unchanged; the hook is a side-channel navigation.
        // 401 → login, 418 → account-unavailable. Shared with the realtime
        // catch-up fetcher (BUG-1015 AC 3) via `applyResponseStatusHandlers` so
        // the two dispatch identically. Same makeRawCall seam + same reason as
        // before: the .raw() escape hatch every bidder action uses must trap
        // these too. Side-channel — the Response is returned unchanged.
        applyResponseStatusHandlers(res, options);
        return res;
      });
    };

  return buildNode(registry) as ApiClient<T>;
}

/** A body that should be sent as-is (no JSON.stringify, no forced JSON header). */
function isRawBody(body: unknown): boolean {
  return (
    typeof body === 'string' ||
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob) ||
    (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) ||
    (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream)
  );
}
